import styled from "@emotion/styled";
import MessageIcon from "@mui/icons-material/Message";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography
} from "@mui/material";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { FILTER_OPERATIONS, useMembers } from "features/members";
import { useMessagesStore } from "features/messages";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const EventCard = styled(Box)(() => ({
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: "12px"
}));

export const ComingEventPanel = () => {
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const clearRecipients = useMessagesStore(state => state.clearRecipients);
  const addMember = useMessagesStore(state => state.addMember);
  const removeMember = useMessagesStore(state => state.removeMember);
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: members, isLoading } = useMembers({
    churchId: selectedChurch?.id,
    queries: {
      filter: {
        date_of_birth: [
          {
            operator: FILTER_OPERATIONS.GREATER_THAN,
            value: moment().format("YYYY-MM-DD")
          },
          {
            operator: FILTER_OPERATIONS.LESS_THAN,
            value: moment().add(1, "week").format("YYYY-MM-DD")
          }
        ]
      }
    }
  });

  const sendMessage = member => {
    clearRecipients();
    removeMember(member.id);
    addRecipient(
      `${member?.id}_${member?.first_name}_${member?.last_name}`,
      member
    );
    addMember(member.id, {
      id: member.id
    });
    navigate("/messages");
  };

  return (
    <Stack
      sx={{
        border: "1px solid rgba(255, 255, 255, 0.5)",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(8px)",
        p: 2,
        borderRadius: 6
      }}
      spacing={2}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 1, color: "#fff", fontWeight: 700 }}
      >
        {t("quickboard.comingEvent")}
      </Typography>
      {!isLoading && (
        <Stack
          spacing={2}
          sx={{
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            "&::-webkit-scrollbar": {
              width: "6px"
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255, 255, 255, 0.1)"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255, 255, 255, 0.5)",
              borderRadius: "3px"
            }
          }}
        >
          {members?.result?.map((member, index) => (
            <EventCard key={index}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Avatar
                  src={`${IMAGE_URL}/${member.profile_image_url}`}
                  alt={member.first_name}
                >
                  {member.first_name.charAt(0)}
                </Avatar>
                <Stack>
                  <Typography variant="subtitle1">
                    {member.first_name} {member.father_name}
                  </Typography>
                  <Typography variant="caption">
                    {moment().diff(moment(member.date_of_birth), "years")}{" "}
                    {t("quickboard.birthday")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="text"
                  size="small"
                  startIcon={<MessageIcon />}
                  onClick={() => sendMessage(member)}
                >
                  {t("quickboard.sendMessage")}
                </Button>
                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                  {moment(member.date_of_birth).format("DD-MM-YYYY")}
                </Typography>
              </Stack>
            </EventCard>
          ))}

          {members?.result?.length === 0 && (
            <Typography variant="subtitle2" sx={{ color: "#fff" }}>
              {t("quickboard.noEventsComing")}
            </Typography>
          )}
        </Stack>
      )}

      {isLoading && (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size="24px" sx={{ color: "#fff" }} />
        </Stack>
      )}
    </Stack>
  );
};
